export type ScreenRect = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

export type PopoverPlacement = {
  side: 'top' | 'bottom';
  // Lifts a popover opened above the add pill clear of the keyboard when the
  // pill itself sits where the keyboard will be
  sideOffset: number;
  // Moves the popover left from the add pill's start edge so it stays on screen
  alignOffset: number;
  height: number;
};

export const EMOJI_POPOVER_WIDTH = 256;
export const EMOJI_POPOVER_HEIGHT = 320;
export const EMOJI_POPOVER_GAP = 4;
// An iPhone's portrait keyboard with its suggestion bar, which search's focus raises
export const PHONE_KEYBOARD_HEIGHT = 336;
const SCREEN_EDGE = 8;

// Where the picker opens on a phone: under the add pill when it clears the
// keyboard, else above it, and shorter only when neither side has its full height
export function phonePopoverPlacement(
  trigger: ScreenRect,
  screen: ScreenRect
): PopoverPlacement {
  const keyboardTop = screen.bottom - PHONE_KEYBOARD_HEIGHT;
  const below = keyboardTop - SCREEN_EDGE - trigger.bottom - EMOJI_POPOVER_GAP;
  const aboveEdge = Math.min(
    trigger.top - EMOJI_POPOVER_GAP,
    keyboardTop - SCREEN_EDGE
  );
  const above = aboveEdge - screen.top - SCREEN_EDGE;
  const side =
    below >= EMOJI_POPOVER_HEIGHT || below >= above ? 'bottom' : 'top';
  const overflow =
    trigger.left + EMOJI_POPOVER_WIDTH - (screen.right - SCREEN_EDGE);
  const shift = Math.max(
    0,
    Math.min(overflow, trigger.left - (screen.left + SCREEN_EDGE))
  );
  return {
    side,
    sideOffset: side === 'bottom' ? EMOJI_POPOVER_GAP : trigger.top - aboveEdge,
    alignOffset: shift > 0 ? -shift : 0,
    height: Math.min(EMOJI_POPOVER_HEIGHT, side === 'bottom' ? below : above),
  };
}

// The phone's screen: the playground's phone frame when the card sits in one,
// else the window
export function phoneScreenOf(element: HTMLElement): ScreenRect {
  const frame = element.closest<HTMLElement>('[data-phone-screen]');
  if (!frame) {
    return {
      top: 0,
      bottom: window.innerHeight,
      left: 0,
      right: window.innerWidth,
    };
  }
  const rect = frame.getBoundingClientRect();
  const left = rect.left + frame.clientLeft;
  return {
    top: Math.max(0, rect.top),
    bottom: window.innerHeight,
    left,
    right: left + frame.clientWidth,
  };
}
